import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { 
  ArrowLeft, Bell, BellOff, Check, CheckCheck, Truck, Package,
  Loader2, AlertCircle, Settings, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useDriverNotifications } from '../../context/DriverNotificationContext';

const DriverNotificationsPage = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    pushEnabled,
    loading,
    isPushSupported,
    subscribeToPush,
    unsubscribeFromPush,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  } = useDriverNotifications();
  
  const [togglingPush, setTogglingPush] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    if (!token) {
      navigate('/driver/login');
      return;
    }
    fetchNotifications();
  }, [navigate, fetchNotifications]);

  const handleTogglePush = async () => {
    setTogglingPush(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        toast.success('Push notifications disabled');
      } else {
        const success = await subscribeToPush();
        if (success) {
          toast.success('Push notifications enabled!');
        } else {
          toast.error('Failed to enable push notifications. Please check browser permissions.');
        }
      }
    } catch (error) {
      toast.error('Failed to update notification settings');
    } finally {
      setTogglingPush(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Navigate based on notification type
    if (notification.data?.load_id) {
      navigate(`/driver/load/${notification.data.load_id}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'load_assigned':
        return <Truck className="w-5 h-5 text-[#4ADE80]" />;
      case 'status_update':
        return <Package className="w-5 h-5 text-blue-400" />;
      default:
        return <Bell className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[#071423] pb-20">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/driver/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-white">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-xs text-[#4ADE80]">{unreadCount} unread</p>
              )}
            </div>
          </div>
          {notifications.length > 0 && unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={markAllAsRead}
              className="text-xs text-gray-400"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Push Notification Settings */}
      <div className="px-4 py-4">
        <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {pushEnabled ? (
                  <Bell className="w-6 h-6 text-[#4ADE80]" />
                ) : (
                  <BellOff className="w-6 h-6 text-gray-400" />
                )}
                <div>
                  <p className="text-white font-medium">Push Notifications</p>
                  <p className="text-xs text-gray-400">
                    {pushEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
              {isPushSupported ? (
                <Switch
                  checked={pushEnabled}
                  onCheckedChange={handleTogglePush}
                  disabled={togglingPush}
                />
              ) : (
                <span className="text-xs text-red-400">Not supported</span>
              )}
            </div>
            {!isPushSupported && (
              <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Push notifications require a modern browser
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notifications List */}
      <div className="px-4">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
          Recent Notifications
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#4ADE80]" />
          </div>
        ) : notifications.length === 0 ? (
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="p-8 text-center">
              <Bell className="w-12 h-12 mx-auto text-gray-500 mb-3" />
              <p className="text-gray-400">No notifications yet</p>
              <p className="text-xs text-gray-500 mt-1">
                You'll be notified when you receive new load assignments
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <Card 
                key={notification.id}
                className={`border-[#3B82F6]/20 ${
                  notification.is_read ? 'bg-[#0D1B2A]/50' : 'bg-[#0D1B2A] border-l-2 border-l-[#4ADE80]'
                } cursor-pointer hover:bg-[#112240] transition-colors`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      notification.is_read ? 'bg-[#0A1628]' : 'bg-[#4ADE80]/10'
                    }`}>
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`font-medium ${
                          notification.is_read ? 'text-gray-400' : 'text-white'
                        }`}>
                          {notification.title}
                        </p>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatTime(notification.created_at)}
                        </span>
                      </div>
                      <p className={`text-sm mt-0.5 ${
                        notification.is_read ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {notification.body}
                      </p>
                      {notification.data?.pickup_location && (
                        <p className="text-xs text-gray-500 mt-1">
                          {notification.data.pickup_location} → {notification.data.delivery_location}
                        </p>
                      )}
                    </div>
                    {!notification.is_read && (
                      <div className="w-2 h-2 rounded-full bg-[#4ADE80] mt-2" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverNotificationsPage;
